/**
 * Artwork for the design fixtures, drawn rather than fetched.
 *
 * The preview fixtures first pointed at `/foyer-assets/demo/*.jpg`, which do not
 * exist and never did — so every channel card and every poster came up blank,
 * and the two screens looked broken in exactly the review that was meant to
 * judge them. A fixture that depends on files nobody has shipped is not a
 * fixture.
 *
 * These are data URIs: no network, no 404, no assets to keep in step with a
 * repo, and identical on a laptop and on a panel behind a hotel firewall. They
 * are deliberately obviously synthetic — flat colour and a word — because a
 * reviewer must never be left wondering whether they are looking at real
 * catalogue art.
 *
 * ⚠️ Preview and tests only. Nothing here is reachable from a bound television.
 */

function dataUri(svg: string): string {
  // `encodeURIComponent` rather than base64: it survives the `#` in a hex
  // colour, which is the one character that silently truncates an unescaped
  // SVG data URI and leaves a blank box behind.
  //
  // ⚠️ And then the three characters `encodeURIComponent` deliberately does NOT
  // escape — `(`, `)` and `'` — have to go too, because this string ends up
  // inside a CSS `url(...)`. An unescaped parenthesis closes that function
  // early, the whole declaration is dropped as invalid, and the element paints
  // NOTHING with no error anywhere. `hsl(...)` in the markup below put a pair
  // in every single image.
  const encoded = encodeURIComponent(svg.trim())
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/'/g, "%27");
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
}

/** Deterministic hue from a string, so a title keeps its colour across renders. */
function hueOf(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * A channel mark, dark on white.
 *
 * White ground on purpose: it is what a real channel logo is supplied on, and
 * the card draws it on white for the same reason. A synthetic logo that sat on
 * a transparent ground would flatter the card by hiding the contrast problem a
 * real one can have.
 */
export function demoChannelLogo(name: string): string {
  const hue = hueOf(name);
  return dataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 96">
  <rect width="240" height="96" fill="#ffffff"/>
  <rect x="8" y="8" width="224" height="80" rx="10" fill="hsl(${hue} 70% 42%)"/>
  <text x="120" y="60" font-family="Helvetica,Arial,sans-serif" font-size="38"
        font-weight="700" fill="#ffffff" text-anchor="middle">${escapeText(
          name
        )}</text>
</svg>`);
}

/**
 * A poster, in the 2:3 shape a catalogue actually serves.
 *
 * The title is drawn ON it rather than left to the card's caption, because a
 * rail of six identical rectangles tells a reviewer nothing about whether the
 * focus ring, the ordering or the paging are working.
 */
export function demoPoster(title: string): string {
  const hue = hueOf(title);
  const words = title.split(/\s+/).slice(0, 3);
  const lines = words
    .map(
      (w, i) =>
        `<text x="200" y="${330 + i * 54}" font-family="Georgia,serif" font-size="44"
           fill="#ffffff" text-anchor="middle" opacity="0.94">${escapeText(w)}</text>`
    )
    .join("");
  return dataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 600">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="hsl(${hue} 55% 38%)"/>
      <stop offset="100%" stop-color="hsl(${(hue + 40) % 360} 60% 14%)"/>
    </linearGradient>
  </defs>
  <rect width="400" height="600" fill="url(#g)"/>
  <circle cx="300" cy="140" r="90" fill="hsl(${(hue + 180) % 360} 70% 60%)" opacity="0.28"/>
  ${lines}
</svg>`);
}

/** A 16:9 still, for the hero behind a programme. */
export function demoStill(title: string): string {
  const hue = hueOf(title);
  return dataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 960 540">
  <defs>
    <linearGradient id="s" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="hsl(${hue} 60% 34%)"/>
      <stop offset="100%" stop-color="hsl(${(hue + 55) % 360} 65% 12%)"/>
    </linearGradient>
  </defs>
  <rect width="960" height="540" fill="url(#s)"/>
  <circle cx="720" cy="180" r="150" fill="hsl(${(hue + 200) % 360} 75% 62%)" opacity="0.22"/>
  <text x="900" y="500" font-family="Helvetica,Arial,sans-serif" font-size="34"
        font-weight="700" fill="#ffffff" opacity="0.5"
        text-anchor="end">${escapeText(title)}</text>
</svg>`);
}
